import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuth } from '../../hooks/useAuth';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import CustomDropdown from '../../components/CustomDropdown';
import './Tasks.css';
import CustomCalendar from '../../components/CustomCalendar';
import { sanitizeHtml } from '../../utils/sanitizeHtml';

const Tasks = () => {
  const { tasks, addTask, updateTask, deleteTask, user, isAdmin, isManager, users, reloadTasks } = useAuth();
  const canManageTasks = isAdmin() || isManager();
  const showTaskPermissionError = () => {
    alert('Недостаточно прав для управления задачами');
  };
  
  const [viewMode, setViewMode] = useState('list');
  const [calendarView, setCalendarView] = useState('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editingTasks, setEditingTasks] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [editingTask, setEditingTask] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [creatingTask, setCreatingTask] = useState(false);
  const [showCompletedTasks, setShowCompletedTasks] = useState(false);
  const [showTaskDetail, setShowTaskDetail] = useState(false);
  const [inlineEditing, setInlineEditing] = useState(null);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const calendarRef = useRef(null);

  const handleInlineDepartmentChange = async (taskId, newDepartment) => {
  if (!canManageTasks) {
    showTaskPermissionError();
    setEditingDepartment(null);
    return;
  }

  // Не разрешаем пустой отдел
  if (!newDepartment || newDepartment.trim() === '') {
    alert('Пожалуйста, выберите отдел');
    setEditingDepartment(null);
    return;
  }
  
  try {
    await updateTask(taskId, {
      department: newDepartment,
      updated_at: new Date().toISOString()
    });
    setEditingDepartment(null);
  } catch (error) {
    alert('Ошибка при изменении отдела: ' + error.message);
  }
};


  // useEffect для закрытия по клику вне элемента
  useEffect(() => {
    const handleClickOutside = (event) => {
      if ((editingDepartment || inlineEditing) && 
          !event.target.closest('.inline-control') &&
          !event.target.closest('.task-preview-department') &&
          !event.target.closest('.custom-dropdown') &&
          !event.target.closest('.dropdown-menu') &&
          !event.target.closest('.inline-calendar-container') &&
          !event.target.closest('.custom-calendar') &&
          !event.target.closest('.react-datepicker')) {
        setEditingDepartment(null);
        setInlineEditing(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [editingDepartment, inlineEditing]);

  // ReactQuill modules
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['clean']
    ],
  };

  const quillRef = useRef(null);
  
  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'list', 'bullet',
    'align',
    'link', 'image'
  ];

  // Опции для кастомных пикеров
  const priorityOptions = [
    { value: 'low', label: 'Низкий' },
    { value: 'medium', label: 'Средний' },
    { value: 'high', label: 'Высокий' }
  ];

  const shiftTypeOptions = [
    { value: 'day', label: 'Дневная' },
    { value: 'night', label: 'Ночная' }
  ];

  const taskStatusOptions = [
    { value: 'pending', label: 'В ожидании' },
    { value: 'in_progress', label: 'В работе' },
    { value: 'completed', label: 'Завершено' }
  ];

  // Функция для проверки срочной задачи
  const isUrgentTask = (task) => {
    if (task.status === 'completed') return false;
    if (!task.dueDate || task.priority !== 'high') return false;
    
    const today = new Date();
    const taskDate = new Date(task.dueDate);
    return today.toDateString() === taskDate.toDateString();
  };

  const formatDateForInput = (date) => {
    if (!date) return '';
    
    if (typeof date === 'string') return date;
    
    const d = new Date(date);
    const moscowTime = new Date(d.getTime() + (3 * 60 * 60 * 1000));
    return moscowTime.toISOString().split('T')[0];
  };

  const formatDateForMoscow = (dateString) => {
    if (!dateString) return 'Неизвестно';
    try {
      const date = new Date(dateString);
      const moscowTime = new Date(date.getTime() + (3 * 60 * 60 * 1000));
      return moscowTime.toLocaleString('ru-RU', {
        timeZone: 'Europe/Moscow',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Неизвестно';
    }
  };

  const formatDateOnlyForMoscow = (dateString) => {
    if (!dateString) return 'Не указан';
    
    try {
      const date = new Date(dateString);
      const moscowTime = new Date(date.getTime() + (3 * 60 * 60 * 1000));
      return moscowTime.toLocaleDateString('ru-RU');
    } catch (error) {
      return 'Не указан';
    }
  };

  const getShiftTypeText = (shiftType) => {
    return shiftType === 'day' ? 'Дневная' : 'Ночная';
  };

  const getShiftTypeIcon = (shiftType) => {
    return shiftType === 'day' ? 'fa-sun' : 'fa-moon';
  };

  const getTodayDate = () => {
    return formatDateForInput(new Date());
  };

  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    priority: 'medium',
    dueDate: getTodayDate(),
    department: '',
    shiftType: 'day'
  });

  const [editTask, setEditTask] = useState({
    title: '',
    description: '',
    priority: 'medium',
    dueDate: '',
    department: '',
    shiftType: 'day'
  });

  // Получение уникальных отделов из пользователей
  const departments = useMemo(() => {
    const deptSet = new Set();
    users.forEach(user => {
      if (user.department && user.department.trim()) {
        deptSet.add(user.department.trim());
      }
    });
    return Array.from(deptSet).sort();
  }, [users]);

  // Опции для выбора отдела при создании задачи
  const createDepartmentOptions = useMemo(() => {
    const options = departments.map(dept => ({
      value: dept,
      label: dept
    }));
    // Добавляем опцию "Все отделы" для новых задач
    return [{ value: 'all', label: 'Все отделы' }, ...options];
  }, [departments]);

  // Опции для фильтра задач (просмотр)
  const filterDepartmentOptions = useMemo(() => {
    const options = departments.map(dept => ({
      value: dept,
      label: dept
    }));
    return [{ value: 'all', label: 'Все отделы' }, ...options];
  }, [departments]);

const editDepartmentOptions = useMemo(() => {
  const options = departments.map(dept => ({
    value: dept,
    label: dept
  }));
  // УБРАТЬ: { value: '', label: 'Без отдела' }
  return [...options];
}, [departments]);

  const getCreatorName = (task) => {
    if (task.creator?.username) {
      return task.creator.username;
    }
    if (user && task.creator_id === user.id) {
      return user.username;
    }
    if (task.creator_id) {
      const creator = users.find(u => u.id === task.creator_id);
      return creator ? creator.username : `ID: ${task.creator_id}`;
    }
    return 'Неизвестно';
  };

  // Мемоизированные вычисления
  const myTasks = useMemo(() => tasks, [tasks]);

  // Фильтрация задач
  const filteredTasks = useMemo(() => 
    myTasks.filter(task => {
      const matchesSearch = task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (task.description && task.description.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesDepartment = departmentFilter === 'all' || task.department === departmentFilter;
      
      if (showCompletedTasks) {
        return matchesSearch && matchesDepartment;
      }
      
      const matchesStatus = statusFilter === 'all' || task.status === statusFilter;
      const isNotCompleted = task.status !== 'completed';
      
      return matchesSearch && matchesStatus && matchesDepartment && isNotCompleted;
    }),
    [myTasks, searchTerm, statusFilter, departmentFilter, showCompletedTasks]
  );

  // Группировка задач по отделам (включая задачи с department='all')
const groupTasksByDepartment = useCallback((tasksList) => {
  const grouped = {};
  
  tasksList.forEach(task => {
    let department = task.department;
    
    // Если задача имеет department='all', она должна отображаться в группе "Все отделы"
    if (department === 'all') {
      department = 'Все отделы';
    }
    // Если department пустой или undefined, пропускаем задачу
    else if (!department || department.trim() === '') {
      return; // Пропускаем задачи без отдела
    }
    
    if (!grouped[department]) {
      grouped[department] = [];
    }
    grouped[department].push(task);
  });
  
  // Сортируем группы: "Все отделы" идет первой, остальные в алфавитном порядке
  const sortedGroups = {};
  const departmentsList = Object.keys(grouped).sort((a, b) => {
    if (a === 'Все отделы') return -1;
    if (b === 'Все отделы') return 1;
    return a.localeCompare(b);
  });
  
  departmentsList.forEach(dept => {
    sortedGroups[dept] = grouped[dept];
  });
  
  return sortedGroups;
}, []);

  // Управление expandedGroups
  const [expandedGroups, setExpandedGroups] = useState({});

  useEffect(() => {
    const initial = {};
    const groupedTasks = groupTasksByDepartment(filteredTasks);
    Object.keys(groupedTasks).forEach(dept => {
      initial[`all_${dept}`] = true;
    });
    setExpandedGroups(initial);
  }, [filteredTasks, groupTasksByDepartment]);

  const toggleGroup = (groupName) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };

  // Функции для календаря
  const goToPrevious = () => {
    if (calendarView === 'month') {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    } else {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() - 7));
    }
  };

  const goToNext = () => {
    if (calendarView === 'month') {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    } else {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() + 7));
    }
  };

  const handleDayClick = (day) => {
    setSelectedDate(day.date);
    setShowTaskModal(true);
  };

  const handleTaskClick = (task) => {
    setSelectedTask(task);
    setEditingTask(null);
    setCreatingTask(false);
    setShowTaskDetail(true);
  };

  const handleCloseTaskDetail = () => {
    setSelectedTask(null);
    setEditingTask(null);
    setCreatingTask(false);
    setShowTaskDetail(false);
  };

  // Функции для работы с задачами
  const handleCreateTask = async (e) => {
  e.preventDefault();

  if (!canManageTasks) {
    showTaskPermissionError();
    return;
  }
  
  if (!newTask.department || newTask.department.trim() === '') {
    alert('Пожалуйста, выберите отдел');
    return;
  }

    
    try {
      const dueDate = new Date(newTask.dueDate);
      dueDate.setHours(12, 0, 0, 0);
      
      // Если выбран "Все отделы", сохраняем как 'all'
      const departmentValue = newTask.department === 'all' ? 'all' : newTask.department;
      
      const createdTask = await addTask({
        title: newTask.title,
        description: newTask.description,
        priority: newTask.priority,
        due_date: dueDate.toISOString(),
        department: departmentValue,
        status: 'pending',
        shift_type: newTask.shiftType,
        creator_id: user.id
      });
      
      setNewTask({
        title: '',
        description: '',
        priority: 'medium',
        dueDate: getTodayDate(),
        department: '',
        shiftType: 'day'
      });
      
      setCreatingTask(false);
      setShowTaskDetail(false);
      
    } catch (error) {
      alert('Ошибка при создании задачи: ' + error.message);
    }
  };

  const handleEditTask = (task, fromCalendar = false) => {
    if (!canManageTasks) {
      showTaskPermissionError();
      return;
    }

    setEditingTask(task.id);
    const dueDate = task.dueDate ? formatDateForInput(task.dueDate) : '';
    
    // Для задач с department='all' отображаем как 'Все отделы'
    const displayDepartment = task.department === 'all' ? 'all' : (task.department || '');
    
    setEditTask({
      title: task.title,
      description: task.description || '',
      priority: task.priority,
      dueDate: dueDate,
      department: displayDepartment,
      shiftType: task.shift_type || 'day'
    });
    
    if (fromCalendar) {
      setShowTaskModal(false);
      setViewMode('list');
      setSelectedTask(task);
      setShowTaskDetail(true);
    }
  };

 const handleSaveEdit = async (taskId) => {
  if (!canManageTasks) {
    showTaskPermissionError();
    return;
  }

  try {
    // Проверяем отдел
    if (!editTask.department || editTask.department.trim() === '') {
      alert('Пожалуйста, выберите отдел');
      return;
    }
      const dueDate = new Date(editTask.dueDate);
      dueDate.setHours(12, 0, 0, 0);
      
      // При сохранении, если выбрано "Все отделы", сохраняем как 'all'
      const departmentValue = editTask.department === 'all' ? 'all' : editTask.department;
      
      await updateTask(taskId, {
        title: editTask.title,
        description: editTask.description,
        priority: editTask.priority,
        due_date: dueDate.toISOString(),
        department: departmentValue,
        shift_type: editTask.shiftType,
        updated_at: new Date().toISOString()
      });
      
      setEditingTask(null);
      setSelectedTask(prev => prev ? { 
        ...prev, 
        ...editTask,
        department: departmentValue 
      } : null);
      
    } catch (error) {
      alert('Ошибка при сохранении задачи: ' + error.message);
    }
  };

  const handleCancelEdit = () => {
    setEditingTask(null);
  };

  const handleCancelCreate = () => {
    setCreatingTask(false);
    setShowTaskDetail(false);
    setNewTask({
      title: '',
      description: '',
      priority: 'medium',
      dueDate: getTodayDate(),
      department: '',
      shiftType: 'day'
    });
  };

  const handleDeleteTask = async (taskId) => {
    if (!canManageTasks) {
      alert('У вас нет прав для удаления задач. Обратитесь к администратору.');
      return;
    }
    
    if (window.confirm('Вы уверены, что хотите удалить эту задачу?')) {
      try {
        await deleteTask(taskId);
        if (selectedTask && selectedTask.id === taskId) {
          setSelectedTask(null);
          setShowTaskDetail(false);
        }
        if (editingTask === taskId) {
          setEditingTask(null);
        }
      } catch (error) {
        alert('Ошибка при удалении задачи');
      }
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    if (!canManageTasks) {
      showTaskPermissionError();
      return;
    }

    try {
      await updateTask(taskId, {
        status: newStatus,
        updated_at: new Date().toISOString()
      });
      
      if (selectedTask && selectedTask.id === taskId) {
        setSelectedTask(prev => ({
          ...prev,
          status: newStatus,
          updated_at: new Date().toISOString()
        }));
      }
      
    } catch (error) {
      alert('Ошибка при изменении статуса задачи: ' + error.message);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return '#ef4444';
      case 'medium': return '#f59e0b';
      case 'low': return '#10b981';
      default: return '#6b7280';
    }
  };

  const getPriorityText = (priority) => {
    switch (priority) {
      case 'high': return 'Высокий';
      case 'medium': return 'Средний';
      case 'low': return 'Низкий';
      default: return 'Не указан';
    }
  };

  // Inline редактирование
  const handleInlineStatusChange = async (taskId, newStatus) => {
    if (!canManageTasks) {
      showTaskPermissionError();
      setInlineEditing(null);
      return;
    }

    try {
      await updateTask(taskId, {
        status: newStatus,
        updated_at: new Date().toISOString()
      });
      
      if (selectedTask && selectedTask.id === taskId) {
        setSelectedTask(prev => ({
          ...prev,
          status: newStatus,
          updated_at: new Date().toISOString()
        }));
      }
      
      setInlineEditing(null);
    } catch (error) {
      alert('Ошибка при изменении статуса задачи: ' + error.message);
    }
  };

  const handleInlinePriorityChange = async (taskId, newPriority) => {
    if (!canManageTasks) {
      showTaskPermissionError();
      setInlineEditing(null);
      return;
    }

    try {
      await updateTask(taskId, {
        priority: newPriority,
        updated_at: new Date().toISOString()
      });
      
      if (selectedTask && selectedTask.id === taskId) {
        setSelectedTask(prev => ({
          ...prev,
          priority: newPriority,
          updated_at: new Date().toISOString()
        }));
      }
      
      setInlineEditing(null);
    } catch (error) {
      alert('Ошибка при изменении приоритета: ' + error.message);
    }
  };

  // Эффект для синхронизации selectedTask с актуальными данными
  useEffect(() => {
    if (selectedTask) {
      const currentTask = tasks.find(task => task.id === selectedTask.id);
      if (currentTask) {
        const currentUpdated = new Date(currentTask.updated_at || currentTask.updatedAt);
        const selectedUpdated = new Date(selectedTask.updated_at || selectedTask.updatedAt);
        
        if (currentUpdated.getTime() > selectedUpdated.getTime()) {
          setSelectedTask(currentTask);
        }
      }
    }
  }, [tasks, selectedTask]);

  // Рендер превью задачи с inline редактированием
const renderTaskPreview = (task) => {
  const urgent = isUrgentTask(task);
  const isCompleted = task.status === 'completed';
  const shortTitle = task.title.length > 30 ? task.title.substring(0, 30) + '...' : task.title;
  
  // Получаем первую строку описания для превью
  let shortDescription = '';
  let hasDescription = false;
  if (task.description && task.description.trim() !== '') {
    // Удаляем HTML-теги для получения текста
    const plainText = task.description.replace(/<[^>]+>/g, '');
    // Заменяем переносы строк и лишние пробелы на одиночный пробел
    const cleanText = plainText.replace(/\n/g, ' ').replace(/\s+/g, ' ').trim();
    if (cleanText) {
      shortDescription = cleanText.substring(0, 30) + (cleanText.length > 30 ? '...' : '');
      hasDescription = true;
    }
  }
  
  // Определяем отображаемое название отдела для текущей задачи
  const displayDepartment = task.department === 'all' ? 'Все отделы' : (task.department || 'Выберите отдел');

  return (
    <div 
      key={`task-${task.id}-${task.dueDate}-${task.updated_at}`}
      className={`task-preview-item ${selectedTask?.id === task.id ? 'selected' : ''} ${urgent ? 'urgent' : ''} ${isCompleted ? 'completed' : ''}`}
      onClick={(e) => {
        if (e.target.closest('.inline-control') || 
            e.target.closest('.department-value') ||
            e.target.closest('.inline-editable') ||
            editingDepartment === `department_${task.id}` ||
            inlineEditing) {
          return;
        }
        handleTaskClick(task);
      }}
    >
      {/* ЗАГОЛОВОК задачи */}
      <div className="task-preview-title">
        {urgent && <div className="urgent-indicator"></div>}
        {shortTitle}
      </div>

      {/* ОПИСАНИЕ - всегда показываем div для сохранения структуры */}
      <div className="task-preview-description-short">
        {hasDescription ? shortDescription : '\u00A0'} {/* &nbsp; для сохранения высоты */}
      </div>

      {/* СТАТУС */}
      <div className="inline-control">
        {canManageTasks && inlineEditing === `status_${task.id}` ? (
          <div className="inline-dropdown-container">
            <CustomDropdown
              options={taskStatusOptions}
              value={task.status}
              onChange={(value) => handleInlineStatusChange(task.id, value)}
              small
            />
          </div>
        ) : (
          <div 
            className={`status-indicator ${canManageTasks ? 'inline-editable' : ''}`}
            onClick={(e) => {
              if (!canManageTasks) {
                return;
              }
              e.stopPropagation();
              setInlineEditing(`status_${task.id}`);
            }}
            title="Кликните для изменения статуса"
          >
            {task.status === 'pending' ? '⏳' : 
             task.status === 'in_progress' ? '🔄' : '✅'}
          </div>
        )}
      </div>

      {/* ПРИОРИТЕТ */}
      <div className="inline-control">
        {canManageTasks && inlineEditing === `priority_${task.id}` ? (
          <div className="inline-dropdown-container">
            <CustomDropdown
              options={priorityOptions}
              value={task.priority}
              onChange={(value) => handleInlinePriorityChange(task.id, value)}
              small
            />
          </div>
        ) : (
          <span 
            className={`priority-badge ${canManageTasks ? 'inline-editable' : ''}`}
            style={{ 
              backgroundColor: getPriorityColor(task.priority),
              color: 'white'
            }}
            onClick={(e) => {
              if (!canManageTasks) {
                return;
              }
              e.stopPropagation();
              setInlineEditing(`priority_${task.id}`);
            }}
            title="Кликните для изменения приоритета"
          >
            {getPriorityText(task.priority)}
          </span>
        )}
      </div>

      {/* ДАТА - СТАТИЧНОЕ ОТОБРАЖЕНИЕ БЕЗ ВОЗМОЖНОСТИ ИЗМЕНЕНИЯ */}
      <div className="inline-control date-control">
        <span className="task-preview-date">
          <i className="far fa-calendar"></i>
          {task.dueDate ? formatDateOnlyForMoscow(task.dueDate) : 'Не указан'}
        </span>
      </div>

      {/* ОТДЕЛ */}
      <div className="task-preview-department">
        {canManageTasks && editingDepartment === `department_${task.id}` ? (
          <div className="inline-dropdown-container">
            <CustomDropdown
              options={[{ value: 'all', label: 'Все отделы' }, ...editDepartmentOptions]}
              value={task.department || ''}
              onChange={(value) => handleInlineDepartmentChange(task.id, value)}
              small
            />
          </div>
        ) : (
          <span 
            className={`department-value ${canManageTasks ? 'inline-editable' : ''}`}
            onClick={(e) => {
              if (!canManageTasks) {
                return;
              }
              e.stopPropagation();
              setEditingDepartment(`department_${task.id}`);
            }}
            title="Кликните для изменения отдела"
          >
            <i className="fas fa-building"></i>
            {displayDepartment}
          </span>
        )}
      </div>
    </div>
  );
};
  const renderDepartmentGroup = (department, tasks, groupType) => {
    const groupKey = `${groupType}_${department}`;
    
    return (
      <div key={department} className="tasks-group">
        <div 
          className="group-header"
          onClick={() => toggleGroup(groupKey)}
        >
          <i className="fas fa-building"></i>
          <h3>{department}</h3>
          <span className="group-count">
            {tasks.length} задач 
            {showCompletedTasks && (
              <span className="completed-count">
                ({tasks.filter(t => t.status === 'completed').length} завершено)
              </span>
            )}
          </span>
          <i className={`fas fa-chevron-down group-toggle ${expandedGroups[groupKey] ? 'rotated' : ''}`}></i>
        </div>
        <div className={`tasks-previews ${expandedGroups[groupKey] ? 'expanded' : ''}`}>
          <div className="tasks-previews-inner">
            {tasks.map(renderTaskPreview)}
          </div>
        </div>
      </div>
    );
  };

  // Рендер формы создания задачи
  const renderCreateTaskForm = () => {
    return (
      <div className="task-detail">
        <div className="task-detail-header">
          <h3>Новая задача</h3>
          <button className="close-detail" onClick={handleCancelCreate}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        
        <div className="task-detail-content">
          <div className="task-detail-meta">
            <div className="meta-item">
              <span className="meta-label">Название *</span>
              <div className="form-controll-grid-tasks">
                <input
                  type="text"
                  value={newTask.title}
                  onChange={(e) => setNewTask({...newTask, title: e.target.value})}
                  className="form-control"
                  placeholder="Введите название задачи"
                  required
                  maxLength={40}
                />
              </div>
            </div>
              
            <div className="meta-item">
              <span className="meta-label">Приоритет *</span>
              <div className="form-controll-grid-tasks">
                <CustomDropdown
                  options={priorityOptions}
                  value={newTask.priority}
                  onChange={(value) => setNewTask({...newTask, priority: value})}
                  placeholder="Выберите приоритет..."
                />
              </div>
            </div>
            
            <div className="meta-item">
              <span className="meta-label">Срок *</span>
              <div className="form-controll-grid-tasks">
                <CustomCalendar
                  value={newTask.dueDate ? new Date(newTask.dueDate) : new Date()}
                  onChange={(date) => {
                    if (date) {
                      const formattedDate = formatDateForInput(date);
                      setNewTask({...newTask, dueDate: formattedDate});
                    }
                  }}
                  placeholder="Выберите дату"
                />
              </div>
            </div>
            
            <div className="meta-item">
              <span className="meta-label">Смена *</span>
              <div className="form-controll-grid-tasks">
                <CustomDropdown
                  options={shiftTypeOptions}
                  value={newTask.shiftType || 'day'}
                  onChange={(value) => setNewTask({...newTask, shiftType: value})}
                  placeholder="Выберите смену..."
                />
              </div>
            </div>
            
            <div className="meta-item">
              <span className="meta-label">Отдел *</span>
              <div className="form-controll-grid-tasks">
                <CustomDropdown
                  options={createDepartmentOptions}
                  value={newTask.department}
                  onChange={(value) => setNewTask({...newTask, department: value})}
                  placeholder="Выберите отдел..."
                />
              </div>
            </div>
          </div>

          <div className="task-detail-description">
            <h4>Описание</h4>
            <div className="quill-editor-container">
              <ReactQuill
                value={newTask.description}
                onChange={(value) => setNewTask({...newTask, description: value})}
                modules={quillModules}
                formats={quillFormats}
                placeholder="Подробное описание задачи..."
                theme="snow"
                style={{ height: '250px' }}
              />
            </div>
          </div>
        </div>

        <div className="task-detail-actions">
          <button 
            className="add-button"
            onClick={handleCreateTask}
          >
            <i className="fas fa-plus"></i>
            Создать задачу
          </button>
        </div>
      </div>
    );
  };

  // Рендер детальной информации о задаче
  const renderTaskDetail = () => {
    if (!selectedTask) return null;

    const urgent = isUrgentTask(selectedTask);
    // Определяем отображаемое название отдела для выбранной задачи
    const displayDepartment = selectedTask.department === 'all' ? 'Все отделы' : (selectedTask.department || 'Не выбран');

    return (
      <div className={`task-detail ${urgent ? 'urgent' : ''}`}>
        <div className="task-detail-header">
          <h2>
            {editingTask === selectedTask.id ? (
              <input
                type="text"
                value={editTask.title}
                onChange={(e) => setEditTask({...editTask, title: e.target.value})}
                className="status-select"
                placeholder="Название задачи"
                maxLength={40}
              />
            ) : (
              selectedTask.title
            )}
          </h2>
          <button className="close-detail" onClick={handleCloseTaskDetail}>
            <i className="fas fa-times"></i>
          </button>
        </div>
          
        <div className="task-detail-content">
          <div className="task-detail-meta">
            <div className="meta-item">
              <span className="meta-label">Приоритет:</span>
              <div className="form-controll-grid-tasks">
                {editingTask === selectedTask.id ? (
                  <CustomDropdown
                    options={priorityOptions}
                    value={editTask.priority}
                    onChange={(value) => setEditTask({...editTask, priority: value})}
                    placeholder="Выберите приоритет..."
                  />
                ) : (
                  <span 
                    className="priority-badge"
                    style={{ 
                      backgroundColor: getPriorityColor(selectedTask.priority),
                      color: 'white'
                    }}
                  >
                    {getPriorityText(selectedTask.priority)}
                  </span>
                )}
              </div>
            </div>
            
            <div className="meta-item">
              <span className="meta-label">Срок:</span>
              <div className="form-controll-grid-tasks">
                {editingTask === selectedTask.id ? (
                  <CustomCalendar
                    value={editTask.dueDate ? new Date(editTask.dueDate) : new Date()}
                    onChange={(date) => {
                      if (date) {
                        const formattedDate = formatDateForInput(date);
                        setEditTask({...editTask, dueDate: formattedDate});
                      }
                    }}
                    placeholder="Выберите дату"
                  />
                ) : (
                  <span className="meta-value">
                    {selectedTask.dueDate ? formatDateOnlyForMoscow(selectedTask.dueDate) : 'Не указан'}
                  </span>
                )}
              </div>
            </div>
            
            <div className="meta-item">
              <span className="meta-label">Смена:</span>
              <div className="form-controll-grid-tasks">
                {editingTask === selectedTask.id ? (
                  <CustomDropdown
                    options={shiftTypeOptions}
                    value={editTask.shiftType || 'day'}
                    onChange={(value) => setEditTask({...editTask, shiftType: value})}
                    placeholder="Выберите смену..."
                  />
                ) : (
                  <span className="meta-value shift-value">
                    {getShiftTypeText(selectedTask.shift_type)}
                  </span>
                )}
              </div>
            </div>
            
            <div className="meta-item">
              <span className="meta-label">Отдел:</span>
              <div className="form-controll-grid-tasks">
               {editingTask === selectedTask.id ? (
  <CustomDropdown
    options={[{ value: 'all', label: 'Все отделы' }, ...editDepartmentOptions]}
    value={editTask.department}
    onChange={(value) => setEditTask({...editTask, department: value})}
    placeholder="Выберите отдел..."
  />
) : (
  <span className="meta-value department-value">
    {displayDepartment || 'Не выбран'}
  </span>
)}
              </div>
            </div>
            
            <div className="meta-item">
              <span className="meta-label">Статус:</span>
              <div className="form-controll-grid-tasks">
                <CustomDropdown
                  options={taskStatusOptions}
                  value={selectedTask.status}
                  onChange={(value) => canManageTasks && editingTask !== selectedTask.id && handleStatusChange(selectedTask.id, value)}
                  placeholder="Выберите статус..."
                  disabled={editingTask === selectedTask.id}
                />
              </div>
            </div>
          </div>

          <div className="task-detail-description">
            <h4>Описание</h4>
            {editingTask === selectedTask.id ? (
              <div className="quill-editor-container">
                <ReactQuill
                  ref={quillRef}
                  value={editTask.description}
                  onChange={(value) => setEditTask({...editTask, description: value})}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Подробное описание задачи..."
                  theme="snow"
                  style={{ height: '250px' }}
                />
              </div>
            ) : (
              selectedTask.description ? (
                <div 
                  className="description-content"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(selectedTask.description) }}
                />
              ) : (
                <p className="no-description">Описание отсутствует</p>
              )
            )}
          </div>

          <div className="task-detail-footer">
            <div className="task-created">
              <i className="far fa-clock"></i>
              Создана: {selectedTask.createdAt ? formatDateForMoscow(selectedTask.createdAt) : 'Неизвестно'}
              {selectedTask.updatedAt && 
               selectedTask.updatedAt !== selectedTask.createdAt && (
                <span style={{marginLeft: '10px', color: '#666'}}>
                  (Изменена: {formatDateForMoscow(selectedTask.updatedAt)})
                </span>
              )}
            </div>
            <div className="task-creator">
              <i className="fas fa-user"></i>
              Автор: {getCreatorName(selectedTask)}
            </div>
          </div>
        </div>

        <div className="task-detail-actions">
          {editingTask === selectedTask.id ? (
            <>
              <button 
                className="add-button"
                onClick={() => handleSaveEdit(selectedTask.id)}
              >
                <i className="fas fa-save"></i>
                Сохранить
              </button>
              <button 
                className="btn-delete"
                onClick={handleCancelEdit}
              >
                <i className="fas fa-times"></i>
                Отмена
              </button>
            </>
          ) : (
            <>
              {canManageTasks && (
                <button 
                  className="btn-delete"
                  onClick={() => handleDeleteTask(selectedTask.id)}
                >
                  <i className="fas fa-trash"></i>
                  Удалить
                </button>
              )}
              <button 
                className="exchange-btn"
                onClick={() => handleEditTask(selectedTask)}
                disabled={!canManageTasks}
              >
                <i className="fas fa-edit"></i>
                Редактировать
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="app-container">
      <div className="tasks-page">
        {/* Заголовок и переключение видов */}
        <div className="content-page-header">
          <div className="search-box">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="Поиск по задачам..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button 
                className="clear-search"
                onClick={() => setSearchTerm('')}
                title="Очистить поиск"
              >
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>

          <div className="null"></div>

          <div className="header-filters">
            <div className="grupped-filters">
              {departments.length > 0 && (
                <CustomDropdown
                  options={filterDepartmentOptions} 
                  value={departmentFilter}
                  onChange={setDepartmentFilter}
                  placeholder="Все отделы..."
                />
              )}
            </div>
            <div className="button_header_tasks">
              {canManageTasks && (
              <button 
                className="add-button"
                onClick={() => {
                  setCreatingTask(true);
                  setSelectedTask(null);
                  setEditingTask(null);
                  setViewMode('list');
                  setShowTaskDetail(true);
                }}
              >
                <i className="fas fa-plus"></i>
                Новая задача
              </button>
              )}
            </div>
          </div>
        </div>

        {/* Основной контент */}
        {viewMode === 'list' && (
          <div className="list-view">
            <div className="tasks-layout">
              <div className="tasks-list-panel">
                <div className="tasks-groups">
                  <div className="tasks-category">
                    <div className="category-header">
                      <i className="fas fa-building"></i>
                      <h2>Задачи по отделам</h2>
                      <div className="category-actions">
                        <button 
                          className={`toggle-archive-btn ${showCompletedTasks ? 'active' : ''}`}
                          onClick={() => setShowCompletedTasks(!showCompletedTasks)}
                          title={showCompletedTasks ? 'Скрыть архивные задачи' : 'Показать архивные задачи'}
                        >
                          <i className={`fas ${showCompletedTasks ? 'fa-eye-slash' : 'fa-archive'}`}></i>
                          {showCompletedTasks ? 'Скрыть архив' : 'Показать архив'}
                        </button>
                      </div>
                    </div>
                    
                    {Object.keys(groupTasksByDepartment(filteredTasks)).length > 0 ? (
                      Object.entries(groupTasksByDepartment(filteredTasks)).map(([department, tasks]) => 
                        renderDepartmentGroup(department, tasks, 'all')
                      )
                    ) : (
                      <div className="no-tasks">
                        <i className="fas fa-tasks"></i>
                        <h3>Задачи не найдены</h3>
                        <p>Попробуйте изменить параметры поиска или создать новую задачу</p>
                        {showCompletedTasks && (
                          <p className="archive-notice">
                            В архиве нет завершенных задач
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {(showTaskDetail && (selectedTask || creatingTask)) && (
                <div className="task-detail-panel">
                  {creatingTask ? renderCreateTaskForm() : renderTaskDetail()}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Tasks;
